import { useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { Label } from '@optools/components/ui/label';
import { ScrollArea } from '@optools/components/ui/scroll-area';
import { Switch } from '@optools/components/ui/switch';
import { cn } from '@optools/components/ui/utils';
import { RESOLVE_ALERT } from '@optools/services/operator-gateway/btc/mutations';
import { BtcSchedulePipeline } from '@optools/services/operator-gateway/btc/types';
import { PipelineAlert } from '@optools/services/operator-gateway/types/generated/graphql';
import { formatWeekdayMonthDayHourMinuteSecond } from '@optools/utils/datetime';
import { isBefore } from 'date-fns';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export interface PipelineAlertsDialogProps {
  alerts: BtcSchedulePipeline['alerts'];
  mediaId: string;
}

export function PipelineAlertsDialog({ alerts, mediaId }: PipelineAlertsDialogProps) {
  const [activeAlertsOnly, setActiveAlertsOnly] = useState(false);
  const [pendingAlertId, setPendingAlertId] = useState<number | null>(null);
  const [resolveAlert, { loading }] = useMutation(RESOLVE_ALERT);

  const sortedAlerts = [...alerts].sort((a, b) => {
    if (a?.createdDateTime === b?.createdDateTime) {
      return 0;
    }
    const aCreated = new Date(a.createdDateTime);
    const bCreated = new Date(b.createdDateTime);
    return isBefore(aCreated, bCreated) ? 1 : -1;
  });

  const filteredAlerts = sortedAlerts.filter((alert) => (activeAlertsOnly ? alert.active : true));
  const activeAlerts = sortedAlerts.filter((alert) => alert.active);

  const handleResolveAlert = ({ id }: PipelineAlert) => {
    const failureMessage = `Alert Resolution Failure - ID: ${id}`;
    setPendingAlertId(id);

    resolveAlert({
      onCompleted: ({ resolveAlert }) => {
        const { reason, success } = resolveAlert;
        if (success) {
          toast.success(`Alert Resolved - ID: ${id}`);
        } else {
          toast.error(failureMessage, { description: reason, duration: Infinity });
        }
        setPendingAlertId(null);
      },
      onError: () => setPendingAlertId(null),
      variables: { alertId: id.toString(), mediaId },
    });
  };

  const alertButtonIcon = alerts?.length ? (
    activeAlerts.length ? (
      <CircleAlert />
    ) : (
      <CircleCheck />
    )
  ) : null;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={activeAlerts.length ? 'destructive' : 'outline'}>
          {alertButtonIcon}
          Pipeline Alerts {activeAlerts.length ? `(${activeAlerts.length})` : null}
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader className="flex-row items-center justify-between pr-8">
          <DialogTitle>Pipeline Alerts</DialogTitle>
          <DialogDescription className="sr-only">
            Resolve pipeline alerts to restore the pipeline state.
          </DialogDescription>
          <Label>
            <Switch checked={activeAlertsOnly} size="sm" onCheckedChange={setActiveAlertsOnly} />
            Active only
          </Label>
        </DialogHeader>

        {filteredAlerts?.length ? (
          <ScrollArea className="max-h-125">
            <ul className="space-y-4">
              {filteredAlerts?.map((alert) => {
                const { active, clearedDateTime, createdDateTime, id, message } = alert;
                return (
                  <li key={`pipeline-alert-detail-${id}`}>
                    <Card
                      className={cn(
                        'flex flex-col gap-8 p-4',
                        active
                          ? 'border-red-800/50 bg-red-800/5'
                          : 'border-green-800/50 bg-green-700/5',
                      )}
                      data-testid={`pipeline-alert-${id}`}>
                      <div className="space-y-1 text-xs">
                        {/* ID */}
                        <div className="flex gap-1">
                          <dt className="font-bold" id="id">
                            ID:
                          </dt>
                          <dd aria-labelledby="id">{id}</dd>
                        </div>

                        {/* Active */}
                        <div className="flex gap-1">
                          <dt className="font-bold" id="active">
                            Active:
                          </dt>
                          <dd aria-labelledby="active">{active ? 'YES' : 'NO'}</dd>
                        </div>

                        {/* Created */}
                        <div className="flex gap-1">
                          <dt className="font-bold" id="created">
                            Created:
                          </dt>
                          <dd aria-labelledby="created">
                            {formatWeekdayMonthDayHourMinuteSecond(createdDateTime)}
                          </dd>
                        </div>

                        {/* Cleared */}
                        <div className="flex gap-1">
                          <dt className="font-bold" id="cleared">
                            Cleared:
                          </dt>
                          <dd aria-labelledby="cleared">
                            {clearedDateTime
                              ? formatWeekdayMonthDayHourMinuteSecond(clearedDateTime)
                              : 'N/A'}
                          </dd>
                        </div>

                        {/* Message */}
                        <div>
                          <dt className="inline font-bold" id="message">
                            Message:{' '}
                          </dt>
                          <dd aria-labelledby="message" className="inline break-all">
                            {message}
                          </dd>
                        </div>

                        {/* Media ID */}
                        <div className="flex gap-1">
                          <dt className="font-bold" id="media-id">
                            Media ID:
                          </dt>
                          <dd aria-labelledby="media-id">{mediaId}</dd>
                        </div>
                      </div>

                      <Button
                        className="ml-auto min-w-32"
                        disabled={loading || !active}
                        loading={loading && pendingAlertId === id}
                        variant={active ? 'destructive' : 'outline'}
                        onClick={() => handleResolveAlert(alert)}>
                        {active ? 'Resolve Alert' : 'Resolved'}
                      </Button>
                    </Card>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        ) : (
          <div className="flex h-48 items-center justify-center">
            <p>There are no {activeAlertsOnly ? 'active alerts' : 'alerts'}.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
